/*
 * MathGraph32 Javascript : Software for animating online dynamic mathematics figures
 * http://www.mathgraph32.org/
 * @Author Yves Biton (yves.biton@sesamath.net)
 * @License: GNU AGPLv3 https://www.gnu.org/licenses/agpl-3.0.html
 */
const Complexe = require('../types/Complexe')
const Opef = require('../types/Opef')
const kernel = require('../kernel/kernel')
const CCb = require('./CCb')
const CCbGlob = require('../kernel/CCbGlob')
module.exports = CPuissance

/**
 * Classe représentant une élévation à une puissance dans un arbre de calcul binaire.
 * @constructor
 * @extends CCb
 * @param {CListeObjets} listeProprietaire : La liste propriétaire.
 * @param {CCb} operande
 * @param {CCb} exposant
 * @returns {CPuissance}
 */
function CPuissance (listeProprietaire, operande, exposant) {
  CCb.call(this, listeProprietaire);
  if (arguments.length !== 1) {
    this.operande = operande;
    this.exposant = exposant;
  }
  this.z1 = new Complexe();
  this.ope1 = new Complexe();
  this.exp1 = new Complexe();
}
CPuissance.prototype = new CCb();
CPuissance.prototype.constructor = CPuissance;
CPuissance.prototype.superClass = "CCb";
CPuissance.prototype.className = "CPuissance";

CPuissance.prototype.nature = function() {
  return CCbGlob.natPuissance;
};
CPuissance.prototype.getClone = function(listeSource, listeCible) {
  var cloneOperande = this.operande.getClone(listeSource, listeCible);
  var cloneExposant = this.exposant.getClone(listeSource, listeCible);
  // à revoir car la fonction a déjà été créée
  return new CPuissance(listeCible, cloneOperande, cloneExposant);
};
CPuissance.prototype.initialisePourDependance = function() {
  CCb.prototype.initialisePourDependance.call(this);
  this.operande.initialisePourDependance();
  this.exposant.initialisePourDependance();
};
CPuissance.prototype.dependDe = function(p) {
    if (this.elementTestePourDependDe === p) return this.dependDeElementTeste;
  return this.memoriseDependance(CCb.prototype.dependDe.call(this,p) ||
    this.operande.dependDe(p) || this.exposant.dependDe(p));
};
CPuissance.prototype.dependDePourBoucle = function(p) {
  return this.operande.dependDePourBoucle(p) || this.exposant.dependDePourBoucle(p);
};
CPuissance.prototype.existe = function(infoRandom) {
  return this.operande.existe(infoRandom) && this.exposant.existe(infoRandom);
};
CPuissance.prototype.resultatBase = function(x, n) {
  if((x === 0) && (n !== 0)) {
    if (n>0) return 0;
    else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
  }
  if (n === 0) {
    if (x === 0)throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    else return 1;
  }
  else
  {
    // Optimisation pour la version 2.0
    // Pour un exposant entier compris entre 1 et 255
    // on ne fait que des produits
    if ((n === Math.floor(n)) && (n > 0) && (n < 256)) return CCbGlob.puisExpEnt(x, n);
    else {
      if (x < 0) {
        if (n === Math.floor(n)) {
          if (n === 2*Math.floor(n/2))
          return Math.exp(n*Math.log(Math.abs(x)));
          else return -Math.exp(n*Math.log(Math.abs(x)));
        }
        else throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      }
      else return Math.exp(n*Math.log(x));
    }
  }
};
CPuissance.prototype.resultat = function(infoRandom) {
  var x1 = this.operande.resultat(infoRandom);
  var n1 = this.exposant.resultat(infoRandom);
  return this.resultatBase(x1, n1);
};
CPuissance.prototype.resultatFonction = function(infoRandom, valeurParametre) {
  var x1 = this.operande.resultatFonction(infoRandom, valeurParametre);
  var n1 = this.exposant.resultatFonction(infoRandom, valeurParametre);
  return this.resultatBase(x1, n1);
};
CPuissance.prototype.resultatComplexeBase = function(ope, exp, zRes) {
  var openull = ope.moduleCarre() === 0;
  var expnull = exp.moduleCarre() === 0;
  if (expnull) {
    if (openull) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
    else {
      zRes.x = 1;
      zRes.y = 0;
    }
  }
  else {
    if (openull) {
      zRes.x = 0;
      zRes.y = 0;
      // Si l'opérande est nul, on regarde si l'exposant est réel ou pas.
      // S'il ne l'est pas, le résultat n'existe pas
      if (exp.y !== 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      else {
        if (exp.x <= 0) throw new Error(kernel.erreurCalculExceptionkernel._digit64_standard);
      }
    }
    else {// Si l'opérande n'est pas nul ni l'exposant
      // On regarde si l'exposant est réel et strictemnt inférieur à 256.
      // Si oui on utilise un algorithme n'utilisant que des produits
      if ((exp.y === 0) && (exp.x === Math.floor(exp.x))) {
        if ((exp.x>0) && (exp.x < 256)) {
          var expent = exp.x;
          ope.puisCompExpEnt(expent, zRes);
        }
        else {
          if ((exp.x<0) && (exp.x > -256)) {
            expent = exp.x;
            ope.puisCompExpEnt(-expent, this.z1);
            this.z1.InverseComp(zRes);
          }
          else ope.puisComp(exp, zRes);
        }
      }
      else ope.puisComp(exp, zRes);
    }
  }
};
CPuissance.prototype.resultatComplexe = function(infoRandom, zRes) {
  this.operande.resultatComplexe(infoRandom, this.ope1);
  this.exposant.resultatComplexe(infoRandom, this.exp1);
  this.resultatComplexeBase(this.ope1, this.exp1, zRes);
};
CPuissance.prototype.resultatFonctionComplexe = function(infoRandom, valeurParametre, zRes) {
  this.operande.resultatFonctionComplexe(infoRandom, valeurParametre, this.ope1);
  this.exposant.resultatFonctionComplexe(infoRandom, valeurParametre, this.exp1);
  this.resultatComplexeBase(this.ope1, this.exp1, zRes);
};
CPuissance.prototype.dependDeVariable = function(ind) {
  return this.operande.dependDeVariable(ind) || this.exposant.dependDeVariable(ind);
};
CPuissance.prototype.getCopie = function() {
  return new CPuissance(this.listeProprietaire, this.operande.getCopie(), this.exposant.getCopie());
};
CPuissance.prototype.chaineCalcul = function(varFor) {
  var chexp = this.exposant.chaineCalcul(varFor);
  var natexp = this.exposant.nature();
  var expestpuis = (natexp === CCbGlob.natPuissance);
  if (natexp === CCbGlob.natFonction) {
    // CFonction fonc = (CFonction) exposant;
    expestpuis = expestpuis || (this.exposant.Opef === Opef.Exp)
      || (this.exposant.Opef === Opef.Expo);
  }
  if (expestpuis) chexp = "(" + chexp + ")";
  var natop = this.operande.nature();
  var opestpuis = (natop === CCbGlob.natPuissance);
  if (opestpuis) return "(" +  this.operande.chaineCalcul(varFor) + ")^" + chexp;
  else return this.operande.chaineCalcul(varFor) + "^" + chexp;
};
CPuissance.prototype.chaineLatex = function(varFor) {
  var parouvLatex = "\\left(";
  var parferLatex = "\\right)";
  var chexp = this.exposant.chaineLatexSansPar(varFor); // Parenthèses inutiles pour le LaTeX
  var natop = this.operande.nature();
  // Bug corrigé version 4.7.2.1
  if (((natop & (CCbGlob.natPuissance | CCbGlob.natOperation | CCbGlob.natMoinsUnaire)) !== 0)
      || ((natop & (CCbGlob.natFonction | CCbGlob.natFonction2Var | CCbGlob.natFonction3Var)) !== 0))
    return parouvLatex +  this.operande.chaineLatexSansPar(varFor) + parferLatex + "^{" + chexp + "}";
    else return this.operande.chaineLatexSansPar(varFor) + "^{" + chexp + "}";
};
CPuissance.prototype.estConstant = function() {
  return this.operande.estConstant() && this.exposant.estConstant();
};
CPuissance.prototype.deriveePossible = function(indiceVariable) {
  return this.operande.deriveePossible(indiceVariable) && this.exposant.deriveePossible(indiceVariable);
};
CPuissance.prototype.calculAvecValeursRemplacees = function() {
  return new CPuissance(this.listeProprietaire, this.operande.calculAvecValeursRemplacees(), this.exposant.calculAvecValeursRemplacees()) ;
};
CPuissance.prototype.read = function(inps, list) {
  CCb.prototype.read.call(this, inps, list);
  this.operande = inps.readObject(list);
  this. exposant = inps.readObject(list);
};
CPuissance.prototype.write = function(oups, list){
  CCb.prototype.write.call(this, oups, list);
  oups.writeObject(this.operande);
  oups.writeObject(this.exposant);
};



// WEBPACK FOOTER //
// ./src/objets/CPuissance.js